import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { FormikProps } from "formik";
import { getUserId } from "../../../Services/auth";
import faradayApi from "../../../Services/faradayApi";
import { buildSimulatorInvoiceData } from "./simulatorInvoiceData";
import { FormValues } from "../types";

export const useProposalGeneration = ({
  formik,
  userEmail,
  simulacaoSelected,
  setShowAlert,
}: {
  formik: FormikProps<FormValues>;
  userEmail: string;
  simulacaoSelected: string;
  setShowAlert: (show: boolean) => void;
}) => {
  const [proposalData, setProposalData] = useState<any>();

  const generateProposalData = () => {
    const proposalData = JSON.parse(
      JSON.stringify({
        todayDate: new Date(Date.now()).toLocaleString("pt-br").split(",")[0],
        userEmail,
        clientName: formik.values.nomeCliente,
        localization: formik.values.localization,
        modules: formik.values.modules,
        inverter: formik.values.inverter,
        structure: formik.values.structure,
        items: formik.values.items,
        lcoe: +(+formik.values.lcoe
          .replace(/\./g, "")
          .replace(",", ".")).toFixed(2),
        potenciaUsina: +(
          +formik.values.potenciaUsina.replace(/\./g, "").replace(",", ".") /
          1000
        ).toFixed(2),
        demandaUsina: +(
          +formik.values.demandaUsina.replace(/\./g, "").replace(",", ".") /
          1000
        ).toFixed(2),
        geracaoMediaAnual: +(
          (+formik.values.geracao.replace(/\./g, "").replace(",", ".") / 1000) *
          12
        ).toFixed(2),
        percentualConsumoAtendido: +(
          (+formik.values.geracao.replace(/\./g, "").replace(",", ".") * 100) /
          (+formik.values.consumoPonta.replace(/\./g, "").replace(",", ".") +
            +formik.values.consumoForaPonta
              .replace(/\./g, "")
              .replace(",", "."))
        ).toFixed(0),
      })
    );

    if (proposalData.percentualConsumoAtendido > 100)
      proposalData.percentualConsumoAtendido = 100;

    proposalData.clientLogo = formik.values.clientLogo;
    proposalData.partnerLogo = formik.values.partnerLogo;
    proposalData.projectImage1 = formik.values.projectImage1;
    proposalData.projectImage2 = formik.values.projectImage2;

    for (const item of proposalData.items) {
      item.potencia = +item.potencia.replace(/\./g, "").replace(",", ".");
      item.geradorFotovoltaico = +item.geradorFotovoltaico
        .replace(/\./g, "")
        .replace(",", ".");
      item.projetoHomologacao = +item.projetoHomologacao
        .replace(/\./g, "")
        .replace(",", ".");
      item.epc = +item.epc.replace(/\./g, "").replace(",", ".");

      item.geradorFotovoltaicoCost = +(
        item.geradorFotovoltaico *
        (1000000 * item.potencia)
      ).toFixed(2);
      item.projetoHomologacaoCost = +(
        item.projetoHomologacao *
        (1000000 * item.potencia)
      ).toFixed(2);
      item.epcCost = +(item.epc * (1000000 * item.potencia)).toFixed(2);

      item.totalValue = +(
        item.geradorFotovoltaico +
        item.projetoHomologacao +
        item.epc
      ).toFixed(2);
      item.totalCost = +(
        item.geradorFotovoltaicoCost +
        item.projetoHomologacaoCost +
        item.epcCost
      ).toFixed(2);

      item.geradorFotovoltaicoCapex = +(
        (item.geradorFotovoltaicoCost * 100) /
        item.totalCost
      ).toFixed(2);
      item.projetoHomologacaoCapex = +(
        (item.projetoHomologacaoCost * 100) /
        item.totalCost
      ).toFixed(2);
      item.epcCapex = +((item.epcCost * 100) / item.totalCost).toFixed(2);
    }

    setProposalData(proposalData);
  };

  const [screenShotChart, setScreenShotChart] = useState("");

  useEffect(() => {
    setTimeout(() => {
      handleScreenShot();
    }, 1000);
  }, [simulacaoSelected]);

  const [selectedFile, setSelectedFiles] = useState<File | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const invoiceInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    invoiceInputRef.current = event.target;
    setSelectedFiles(event.target.files?.[0] ?? null);
    setShowAlert(false);
  };

  const handleUploadMerx = async (): Promise<string> => {
    setFileLoading(true);

    const userId = getUserId();

    try {
      const dataUpload = buildSimulatorInvoiceData(
        formik.values,
        userId ?? "398e4bf9-fa2f-447e-a285-6953523487e2"
      );
      const response = await faradayApi.post(
        `simulator-invoice/merx`,
        dataUpload
      );
      setFileLoading(false);
      setSelectedFiles(null);
      return response?.data?.id ?? "";
    } catch (error) {
      console.log(error);
      window.alert("Erro ao enviar sua simulação, por favor, tente novamente!");
      setFileLoading(false);
      setSelectedFiles(null);
      return "";
    }
  };

  const handleUpload = async (): Promise<string> => {
    try {
      setFileLoading(true);

      const userId = getUserId();
      if (!selectedFile) {
        window.alert("Selecione alguma fatura para enviar!");
        setFileLoading(false);
        return "";
      }

      const formData = new FormData();

      formData.append("file", selectedFile);
      const { simulationData, ...fields } = buildSimulatorInvoiceData(
        formik.values,
        userId ?? ""
      );
      Object.entries(fields).forEach(([key, value]) =>
        formData.append(key, value)
      );
      formData.append("simulationData", JSON.stringify(simulationData));

      const response = await faradayApi.post(`simulator-invoice`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFileLoading(false);

      setSelectedFiles(null);
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
      return response?.data?.id ?? "";
    } catch (error) {
      console.log(error);
      window.alert("Erro ao enviar sua fatura, por favor, tente novamente!");
      setFileLoading(false);
      setSelectedFiles(null);
      return "";
    }
  };

  const handleScreenShot = async () => {
    const element = document.querySelector<HTMLElement>(".simulator-vpl");
    if (!element) return;
    const canvas = await html2canvas(element, {
      scale: 3,
      allowTaint: false,
      useCORS: true,
    });
    const data = canvas.toDataURL("image/jpeg");

    setScreenShotChart(data);
  };

  const handleUploadFortlev = async (): Promise<string> => {
    const userId = getUserId();

    try {
      const dataUpload = buildSimulatorInvoiceData(
        formik.values,
        userId ?? "398e4bf9-fa2f-447e-a285-6953523487e2"
      );
      const response = await faradayApi.post(
        `simulator-invoice/merx`,
        dataUpload
      );
      return response?.data?.id ?? "";
    } catch (error) {
      console.error(error);
      window.alert("Erro ao enviar sua simulação, por favor, tente novamente!");
      return "";
    }
  };

  return {
    proposalData,
    screenShotChart,
    selectedFile,
    fileLoading,
    generateProposalData,
    handleFileSelect,
    handleUpload,
    handleUploadMerx,
    handleUploadFortlev,
  };
};
